import { Box, Text } from 'ink';
import { useEffect, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import type { User } from 'firebase/auth';
import WebSocket from 'ws';
import LoadingText from '../../../components/LoadingText';
import { currentUser } from '../../../utilities/firestore';
import db from '../../../utilities/firestore';
import {
  startIntrospectionSession,
  SessionResponse,
} from '../utilities/sessionManager';
import {
  connectToTunnel as createWebSocketConnection,
  isControlMessage,
  parseControlMessage,
} from '../utilities/websocketClient';
import {
  enterInteractiveMode,
  restoreTerminal,
} from '../utilities/terminalManager';
import { startTokenRefreshMonitoring } from '../utilities/tokenRefresh';
import useExit from '../../../utilities/useExit';

type SessionStatus =
  | 'starting-session'
  | 'waiting'
  | 'connecting'
  | 'connected'
  | 'error'
  | 'closed';

export default function IntrospectSession({
  buildId,
  platform,
  verbose,
  appId,
}: {
  buildId: string;
  platform: 'mac' | 'windows' | 'linux';
  verbose: boolean;
  appId?: string;
}) {
  const exit = useExit();
  const exitRef = useRef(exit);
  useEffect(() => {
    exitRef.current = exit;
  }, [exit]);
  const [status, setStatus] = useState<SessionStatus>('starting-session');
  const [error, setError] = useState<string>('');
  const [tunnelUrl, setTunnelUrl] = useState<string>('');
  const [jti, setJti] = useState<string>('');
  const socketRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const tokenRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRetryingRef = useRef(false);
  const hasEndedRef = useRef(false);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let timeoutTimer: NodeJS.Timeout | null = null;

    function cleanup() {
      if (timeoutTimer) {
        clearTimeout(timeoutTimer);
        timeoutTimer = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
        tokenRefreshIntervalRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      retryCountRef.current = 0;
      isRetryingRef.current = false;
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
      const socket = socketRef.current;
      if (socket) {
        socket.removeAllListeners();
        socket.once('error', () => {});
        try {
          if (socket.readyState === WebSocket.OPEN) {
            socket.close();
          } else if (socket.readyState === WebSocket.CONNECTING) {
            socket.once('open', () => {
              try {
                socket.close();
              } catch {
                // ignore
              }
            });
            socket.terminate();
          } else {
            socket.terminate();
          }
        } catch {
          // ignore
        }
        socketRef.current = null;
      }
      restoreTerminal();
    }

    function exitSession({
      message,
      isError = false,
    }: {
      message?: string;
      isError?: boolean;
    } = {}) {
      if (hasEndedRef.current) {
        return;
      }

      hasEndedRef.current = true;
      cleanup();

      if (message) {
        const output = `\n${message}`;
        if (isError) {
          console.error(output);
        } else {
          console.info(output);
        }
      }

      const exitFn = exitRef.current;
      if (isError) {
        exitFn(
          new Error(message ?? 'Introspection session ended with an error'),
        );
      } else {
        exitFn();
      }
    }

    async function startSession() {
      try {
        const user = currentUser();
        if (!user) {
          throw new Error('Not authenticated. Please run: todesktop login');
        }

        setStatus('starting-session');

        const sessionResponse = await startIntrospectionSession(
          buildId,
          platform,
          appId,
        );

        setJti(sessionResponse.jti);

        if (verbose) {
          console.info(
            `\nSession started (JTI: ${
              sessionResponse.jti
            }, expires: ${new Date(
              sessionResponse.expiresAt * 1000,
            ).toISOString()})\n`,
          );
        }

        if (sessionResponse.tunnelUrl) {
          await connectToTunnel(
            { ...sessionResponse, tunnelUrl: sessionResponse.tunnelUrl },
            user as User,
          );
        } else {
          setStatus('waiting');

          const buildDocRef = doc(db, sessionResponse.buildPath);

          timeoutTimer = setTimeout(
            () => {
              exitSession({
                message:
                  `Timeout waiting for ${platform} introspection to be ready.\n` +
                  `The ${platform} build may not have started yet.`,
                isError: true,
              });
            },
            5 * 60 * 1000,
          );

          unsubscribe = onSnapshot(buildDocRef, async (snapshot) => {
            const data = snapshot.data();
            const platformData = data?.introspect?.[platform];

            if (!platformData || hasEndedRef.current) return;

            const { breakpointStatus } = platformData;
            const { shellStatus } = platformData;

            if (
              (shellStatus === 'ready' || shellStatus === 'disconnected') &&
              platformData.tunnelUrl
            ) {
              if (timeoutTimer) {
                clearTimeout(timeoutTimer);
                timeoutTimer = null;
              }
              await connectToTunnel(
                { ...sessionResponse, tunnelUrl: platformData.tunnelUrl },
                user as User,
              );
            } else if (
              shellStatus === 'error' ||
              breakpointStatus === 'error'
            ) {
              exitSession({
                message: `Introspection error: ${
                  platformData.error ?? 'Unknown error'
                }`,
                isError: true,
              });
            } else if (breakpointStatus === 'finished') {
              exitSession({
                message: 'Build has finished; introspection unavailable',
                isError: true,
              });
            }
          });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        setStatus('error');
        exitSession({ message, isError: true });
      }
    }

    async function connectToTunnel(
      sessionResponse: SessionResponse & { tunnelUrl: string },
      user: User,
    ) {
      const tunnelUrlValue = sessionResponse.tunnelUrl;
      setTunnelUrl(tunnelUrlValue);

      const maxRetries = 5;
      const baseRetryDelayMs = 1000;
      const retryableErrorCodes = new Set([
        'ENOTFOUND',
        'EAI_AGAIN',
        'ECONNREFUSED',
        'ECONNRESET',
        'ETIMEDOUT',
      ]);

      const shouldRetry = (err: NodeJS.ErrnoException) => {
        if (retryCountRef.current >= maxRetries) {
          return false;
        }

        const code = err.code?.toUpperCase();
        if (code && retryableErrorCodes.has(code)) {
          return true;
        }

        const message = err.message?.toLowerCase?.() ?? '';
        return ['enotfound', 'eai_again', 'econnrefused', 'etimedout'].some(
          (indicator) => message.includes(indicator),
        );
      };

      const attemptConnection = (attempt: number) => {
        setStatus('connecting');
        retryCountRef.current = attempt;

        if (verbose) {
          const attemptLabel = attempt === 1 ? 'initial' : `retry ${attempt}`;
          console.info(
            `[Introspect] Starting ${attemptLabel} connection attempt to ${tunnelUrlValue}`,
          );
        }

        const socket = createWebSocketConnection(tunnelUrlValue);
        socketRef.current = socket;

        socket.on('open', async () => {
          retryCountRef.current = 0;
          socket.send(
            JSON.stringify({
              type: 'auth',
              idToken: await user.getIdToken(true),
              jti: sessionResponse.jti,
              buildId,
              platform,
              userId: user.uid,
              appId: sessionResponse.appId,
            }),
          );
        });

        socket.on('message', async (data) => {
          const dataStr = data.toString();

          if (isControlMessage(dataStr)) {
            const message = parseControlMessage(dataStr);

            if (message.type === 'auth-ack') {
              if (message.status === 'ok') {
                setStatus('connected');
                pingIntervalRef.current = enterInteractiveMode(socket);
                tokenRefreshIntervalRef.current = startTokenRefreshMonitoring(
                  user,
                  verbose,
                );
              } else {
                const authMessage = `Authentication failed: ${message.message}`;
                setError(authMessage);
                setStatus('error');
                exitSession({ message: authMessage, isError: true });
              }
            } else if (message.type === 'error') {
              if (message.fatal) {
                const fatalMessage = message.message ?? 'Server error';
                setError(fatalMessage);
                setStatus('error');
                exitSession({ message: fatalMessage, isError: true });
              } else {
                console.error(`\n[Server error] ${message.message}\n`);
              }
            } else if (message.type === 'state') {
              if (verbose) {
                console.info(
                  `\n[Server state] Platform: ${message.platform}, WD: ${message.workingDirectory}, Shell: ${message.shell}\n`,
                );
              }
            }
          } else {
            process.stdout.write(dataStr);
          }
        });

        socket.on('error', (socketErr) => {
          const socketError = socketErr as NodeJS.ErrnoException;

          if (shouldRetry(socketError)) {
            const nextAttempt = retryCountRef.current + 1;
            const delay = Math.min(5000, baseRetryDelayMs * nextAttempt);
            isRetryingRef.current = true;

            if (verbose) {
              console.info(
                `[Introspect] Connection attempt ${
                  retryCountRef.current
                } failed (${
                  socketError.code ?? socketError.message
                }). Retrying in ${delay}ms...`,
              );
            } else {
              console.info(
                `Connection failed (${
                  socketError.code ?? socketError.message
                }). Retrying (${nextAttempt}/${maxRetries})...`,
              );
            }

            if (retryTimeoutRef.current) {
              clearTimeout(retryTimeoutRef.current);
            }

            if (socketRef.current) {
              socketRef.current.removeAllListeners();
              try {
                socketRef.current.terminate();
              } catch {
                // ignore
              }
            }

            retryTimeoutRef.current = setTimeout(() => {
              isRetryingRef.current = false;
              attemptConnection(nextAttempt);
            }, delay);

            return;
          }

          const errorMessage = `WebSocket error: ${socketError.message}`;
          setError(errorMessage);
          setStatus('error');
          exitSession({ message: errorMessage, isError: true });
        });

        socket.on('close', (code, reason) => {
          if (isRetryingRef.current || hasEndedRef.current) {
            return;
          }

          setStatus('closed');

          const reasonText = reason?.toString().trim();
          const message =
            reasonText && reasonText.length > 0
              ? `Connection closed (code ${code}): ${reasonText}`
              : 'Connection closed';

          exitSession({ message });
        });
      };

      try {
        attemptConnection(1);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        setStatus('error');
        exitSession({ message, isError: true });
      }
    }

    startSession();

    return () => {
      if (!hasEndedRef.current) {
        cleanup();
      }
    };
  }, [appId, buildId, platform, verbose]);

  if (status === 'starting-session') {
    return (
      <Box>
        <LoadingText text="Starting introspection session" />
      </Box>
    );
  }

  if (status === 'waiting') {
    return (
      <Box flexDirection="column">
        <Text color="green">✓ Session started</Text>
        {verbose && jti && <Text color="gray">JTI: {jti}</Text>}
        <LoadingText
          text={`Waiting for ${platform} introspection to be ready`}
        />
        <Text color="gray">(timeout in 5 minutes)</Text>
      </Box>
    );
  }

  if (status === 'connecting') {
    return (
      <Box flexDirection="column">
        <Text color="green">✓ Session started</Text>
        {verbose && jti && <Text color="gray">JTI: {jti}</Text>}
        {verbose && tunnelUrl && <Text color="gray">Tunnel: {tunnelUrl}</Text>}
        <LoadingText text="Connecting to build environment" />
      </Box>
    );
  }

  if (status === 'connected') {
    return (
      <Box flexDirection="column">
        <Text color="green">✓ Connected to {platform} build agent</Text>
        <Text color="gray">
          Press Ctrl+D or type &apos;exit&apos; to disconnect
        </Text>
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Box>
        <Text color="red">✗ Error: {error}</Text>
      </Box>
    );
  }

  if (status === 'closed') {
    return (
      <Box>
        <Text color="yellow">Connection closed</Text>
      </Box>
    );
  }

  return null;
}
